/**
 * EmptyState - Placeholder for empty lists and states
 *
 * Provides a consistent empty state pattern with:
 * - Customizable icon, title, and description
 * - Optional call-to-action button
 * - Pre-built variants for common athlete use cases
 */

import { ReactNode } from 'react';
import {
  Video,
  Trophy,
  Film,
  BookOpen,
  Users,
  FolderOpen,
  Search,
  Upload,
  type LucideIcon,
} from 'lucide-react';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
}

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  children?: ReactNode;
  compact?: boolean;
}

export function EmptyState({
  icon: Icon = FolderOpen,
  title,
  description,
  action,
  children,
  compact = false,
}: EmptyStateProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: compact ? '32px 24px' : '48px 24px',
        textAlign: 'center',
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      <div
        style={{
          width: compact ? 56 : 72,
          height: compact ? 56 : 72,
          borderRadius: '50%',
          background: 'var(--gray-100)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: compact ? 16 : 20,
        }}
      >
        <Icon
          size={compact ? 28 : 32}
          style={{ color: 'var(--gray-400)' }}
          aria-hidden="true"
        />
      </div>

      <h3
        style={{
          margin: 0,
          fontSize: compact ? 16 : 18,
          fontWeight: 600,
          color: 'var(--gray-900)',
        }}
      >
        {title}
      </h3>

      {description && (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 14,
            color: 'var(--gray-500)',
            maxWidth: 320,
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
      )}

      {action && (
        <button
          onClick={action.onClick}
          style={{
            marginTop: compact ? 20 : 24,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: 500,
            color: 'white',
            background: 'var(--accent)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
          }}
        >
          {action.icon && <action.icon size={18} aria-hidden="true" />}
          {action.label}
        </button>
      )}

      {children}
    </div>
  );
}

// Pre-built variants for athlete app
interface VariantProps {
  onAction?: () => void;
  compact?: boolean;
}

EmptyState.Videos = function VideosEmptyState({ onAction, compact }: VariantProps) {
  return (
    <EmptyState
      icon={Video}
      title="No videos yet"
      description="Upload your first video to start getting feedback from your coach"
      action={onAction ? { label: 'Upload Video', onClick: onAction, icon: Upload } : undefined}
      compact={compact}
    />
  );
};

EmptyState.Challenges = function ChallengesEmptyState({ compact }: VariantProps) {
  return (
    <EmptyState
      icon={Trophy}
      title="No active challenges"
      description="Check back soon for new challenges to compete in!"
      compact={compact}
    />
  );
};

EmptyState.Highlights = function HighlightsEmptyState({ onAction, compact }: VariantProps) {
  return (
    <EmptyState
      icon={Film}
      title="No highlight reels yet"
      description="Create your first highlight reel to share your best plays"
      action={onAction ? { label: 'Create Reel', onClick: onAction } : undefined}
      compact={compact}
    />
  );
};

EmptyState.Programs = function ProgramsEmptyState({ compact }: VariantProps) {
  return (
    <EmptyState
      icon={BookOpen}
      title="No programs assigned"
      description="Your coach will assign training programs to you here"
      compact={compact}
    />
  );
};

EmptyState.Teams = function TeamsEmptyState({ compact }: VariantProps) {
  return (
    <EmptyState
      icon={Users}
      title="No teams yet"
      description="You'll see your teams here once you're added to one"
      compact={compact}
    />
  );
};

EmptyState.SearchResults = function SearchResultsEmptyState({ compact }: VariantProps) {
  return (
    <EmptyState
      icon={Search}
      title="No results found"
      description="Try adjusting your search or filters"
      compact={compact}
    />
  );
};

export default EmptyState;
